import React from "react";
import { Lock, Sparkles, Clock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import SignalBadge from "@/components/common/SignalBadge";

/**
 * Deep AI signal badge for the screener "Deep AI" column.
 *
 * States rendered:
 *   - Free / Basic user        → 🔒 padlock + upsell tooltip
 *   - Premium, no analysis     → "—" placeholder
 *   - Premium, fresh analysis  → coloured `SignalBadge` (same colour
 *                                scheme as the regular AI Signal column)
 *   - Premium, expired (v13.1) → coloured badge wrapped in a muted/
 *                                grayscale overlay with a small ↻ clock
 *                                glyph + tooltip showing the staleness.
 *
 * Props:
 *   signal       — "STRONG_BUY" | "BUY" | "WATCH" | "HOLD" | "AVOID" | …
 *   isPremium    — boolean. Free users see 🔒.
 *   generatedAt  — ISO timestamp (string|Date).
 *   isExpired    — boolean. v13.1 — render in muted/stale style.
 *   validUntil   — ISO timestamp; used to compute "expired N days ago".
 */
const DeepAISignalBadge = ({
  signal,
  isPremium = false,
  generatedAt = null,
  isExpired = false,
  validUntil = null,
}) => {
  const normalised = signal ? String(signal).toUpperCase().replace(/[\s-]/g, "_") : null;

  // ---------- Free / Basic ----------
  if (!isPremium) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 text-muted-foreground/70 cursor-help"
            data-testid="deep-ai-locked"
          >
            <Lock className="h-3.5 w-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          Deep AI signals are available to all signed-in users during the beta.
        </TooltipContent>
      </Tooltip>
    );
  }

  // ---------- No analysis ----------
  if (!normalised) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="text-muted-foreground/60 text-xs cursor-help"
            data-testid="deep-ai-empty"
          >
            —
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          No deep analysis yet. Run analysis on the stock detail page.
        </TooltipContent>
      </Tooltip>
    );
  }

  // ---------- Has analysis ----------
  // Compute staleness for the tooltip (e.g. "Expired 5 days ago").
  const generatedDate = generatedAt ? new Date(generatedAt) : null;
  const validUntilDate = validUntil ? new Date(validUntil) : null;
  const daysSinceGenerated = generatedDate
    ? Math.max(0, Math.floor((Date.now() - generatedDate.getTime()) / 86_400_000))
    : null;
  const daysSinceExpired = validUntilDate
    ? Math.max(0, Math.floor((Date.now() - validUntilDate.getTime()) / 86_400_000))
    : null;

  const dataTestId = `deep-ai-${normalised.toLowerCase()}${isExpired ? "-expired" : ""}`;

  // v13.1 — Expired styling: wrap the badge in a muted container with a
  // clock glyph overlay. We use opacity + grayscale so the underlying
  // SignalBadge colour is still hinted at, just clearly faded.
  const badgeWrapperClass = isExpired
    ? "relative inline-flex items-center gap-1 opacity-60 saturate-50 cursor-help"
    : "cursor-help inline-flex";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span data-testid={dataTestId} className={badgeWrapperClass}>
          <SignalBadge signal={normalised} size="sm" />
          {isExpired && (
            <Clock
              className="h-3 w-3 text-amber-600 dark:text-amber-400"
              aria-label="Expired — re-run recommended"
              data-testid="deep-ai-expired-glyph"
            />
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="h-3 w-3 text-amber-400" />
          <span className="font-medium">Deep Analysis verdict</span>
          {isExpired && (
            <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-[10px] font-medium">
              <Clock className="h-2.5 w-2.5" />
              EXPIRED
            </span>
          )}
        </div>
        <span className="opacity-80">
          From the 6-specialist AI coordinator (value · quality · dividend · risk · geopolitical · macro).
        </span>
        {generatedDate && (
          <div className="mt-1 text-[10px] opacity-70">
            Generated: {generatedDate.toLocaleDateString()}
            {daysSinceGenerated !== null && ` (${daysSinceGenerated} day${daysSinceGenerated === 1 ? "" : "s"} ago)`}
          </div>
        )}
        {isExpired && daysSinceExpired !== null && (
          <div className="mt-0.5 text-[10px] text-amber-700 dark:text-amber-300">
            Expired {daysSinceExpired} day{daysSinceExpired === 1 ? "" : "s"} ago — re-run for fresh insights.
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

export default DeepAISignalBadge;
